import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PhoneInput } from "@/components/PhoneInput";
import { PrintPreview, DocHeader } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy } from "@/lib/format";

const EMPTY = { referral_type: "doctor", name: "", contact_person: "", phone: "", email: "",
  address: "", city: "", speciality: "", registration_no: "", percentage: "0", notes: "" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];

export default function Referrals() {
  const { can, user } = useAuth();
  const isAdmin = ["business_admin", "hospital_admin", "pharmacy_admin", "platform_owner"].includes(user?.role);
  const now = new Date();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState([]);
  const [print, setPrint] = useState(false);
  const [business, setBusiness] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/referrals");
      setItems(data.items);
    } catch (e) { toast.error(apiError(e)); setItems([]); }
  }, []);

  const loadReport = useCallback(async () => {
    setReport(null);
    try {
      const { data } = await api.get("/referrals/report/monthly", { params: { month, year } });
      setReport(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [month, year]);

  useEffect(() => {
    if (!can("referrals.view")) return;
    load(); loadReport();
    api.get("/settings/full").then(({ data }) => setBusiness(data.business || null)).catch(() => {});
  }, [load, loadReport, can]);

  if (!can("referrals.view")) return <NoAccess what="referrals" />;

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    const body = { ...form, percentage: Number(form.percentage || 0) };
    try {
      if (editing) await api.patch(`/referrals/${editing}`, body);
      else await api.post("/referrals", body);
      toast.success(editing ? "Referral updated." : "Referral added.");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const toggleStatus = async () => {
    try {
      await api.post(`/referrals/${confirm.referral_id}/status`, {
        status: confirm.status === "active" ? "inactive" : "active", reason });
      toast.success("Referral updated.");
      setConfirm(null); setReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const markPaid = async (paid) => {
    if (!selected.length) { toast.error("Select at least one referral entry."); return; }
    try {
      await api.post("/referrals/earnings/mark-paid", { earning_ids: selected, paid });
      toast.success(paid ? "Marked as paid." : "Marked as unpaid.");
      setSelected([]); loadReport();
    } catch (err) { toast.error(apiError(err)); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get("/referrals/report/export", { params: { month, year, fmt },
        responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `referrals-${year}-${String(month).padStart(2, "0")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="referrals-page">
      <PageHeader overline="Business" title="Referrals"
        subtitle="Referring doctors and hospitals, their percentage, and what they have earned each month."
        actions={can("referrals.manage") && (
          <button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
            className={primaryBtnCls} data-testid="add-referral-btn">Add referral</button>
        )} />

      {!items ? <Loading /> : (
        <Table testid="referrals-table"
          columns={["Name", "Type", "Speciality", "Contact", "Referral %", "Status", ""]}
          rows={items} empty={<EmptyState title="No referrals yet" subtitle="Add a referring doctor or hospital to start tracking referral earnings." testid="referrals-empty" />}
          renderRow={(r) => (
            <tr key={r.referral_id} data-testid={`referral-row-${r.referral_id}`}>
              <Td className="font-medium text-slate-900">{r.name}
                <span className="block text-xs text-slate-400">{r.city || "—"}</span></Td>
              <Td className="capitalize text-slate-600">{r.referral_type}</Td>
              <Td className="text-slate-600">{r.speciality || "—"}</Td>
              <Td className="text-slate-600">{r.phone || "—"}
                <span className="block text-xs text-slate-400">{r.email || "—"}</span></Td>
              <Td className="font-semibold text-slate-900">{r.percentage}%</Td>
              <Td>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                  r.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {r.status === "active" ? "Active" : "Inactive"}
                </span>
              </Td>
              <Td>
                <div className="flex flex-wrap justify-end gap-2">
                  {can("referrals.manage") && (
                    <button className={ghostBtnCls} data-testid={`edit-referral-${r.referral_id}`}
                      onClick={() => { setEditing(r.referral_id); setFormError("");
                        setForm({ ...EMPTY, ...r, percentage: String(r.percentage ?? 0) }); setOpen(true); }}>
                      Edit
                    </button>
                  )}
                  {isAdmin && can("referrals.manage") && (
                    <button className={ghostBtnCls} data-testid={`toggle-referral-${r.referral_id}`}
                      onClick={() => { setConfirm(r); setReason(""); }}>
                      {r.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          )} />
      )}

      <div className="mt-12">
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <div>
            <p className="overline text-[#EA580C]">Monthly referral report</p>
            <h2 className="font-ui text-base font-bold md:text-lg">
              {MONTHS[month - 1]} {year}
            </h2>
          </div>
          <select className={`${inputCls} sm:!w-44`} value={month} data-testid="referral-month-select"
            onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" className={`${inputCls} sm:!w-28`} value={year} data-testid="referral-year-input"
            onChange={(e) => setYear(Number(e.target.value))} />
          <button onClick={() => setPrint(true)} className={ghostBtnCls} data-testid="referral-print-btn">Print</button>
          <button onClick={() => download("csv")} className={ghostBtnCls} data-testid="referral-csv-btn">CSV</button>
          <button onClick={() => download("xlsx")} className={ghostBtnCls} data-testid="referral-excel-btn">Excel</button>
          {can("referrals.manage") && (
            <>
              <button onClick={() => markPaid(true)} className={primaryBtnCls} data-testid="referral-mark-paid-btn">
                Mark paid ({selected.length})
              </button>
              <button onClick={() => markPaid(false)} className={ghostBtnCls} data-testid="referral-mark-unpaid-btn">
                Mark unpaid
              </button>
            </>
          )}
        </div>

        {!report ? <Loading /> : (
          <>
            <div className="mb-6 grid gap-3 rounded-lg bg-[#0F172A] p-5 text-white sm:grid-cols-5">
              {[["Invoices", report.totals.invoices, "referral-total-invoices"],
                ["Bill amount", inr(report.totals.bill_amount), "referral-total-bills"],
                ["Referral earned", inr(report.totals.referral_amount), "referral-total-earned"],
                ["Paid", inr(report.totals.paid), "referral-total-paid"]].map(([k, v, t]) => (
                <div key={k}><p className="overline text-slate-400">{k}</p>
                  <p className="mt-1 font-semibold" data-testid={t}>{v}</p></div>
              ))}
              <div><p className="overline text-[#EA580C]">Unpaid</p>
                <p className="mt-1 font-ui text-xl font-bold" data-testid="referral-total-unpaid">{inr(report.totals.unpaid)}</p></div>
            </div>

            <Table testid="referral-report-table"
              columns={["", "Date", "Referral", "Invoice", "Customer", "Bill", "%", "Referral", "Status"]}
              rows={report.rows}
              empty={<EmptyState title="No referral earnings this month" subtitle="Select a referral on a POS bill to start tracking." testid="referral-report-empty" />}
              renderRow={(r) => (
                <tr key={r.earning_id} data-testid={`referral-earning-${r.earning_id}`}>
                  <Td>
                    <input type="checkbox" checked={selected.includes(r.earning_id)}
                      data-testid={`select-earning-${r.earning_id}`}
                      onChange={(e) => setSelected(e.target.checked
                        ? [...selected, r.earning_id]
                        : selected.filter((x) => x !== r.earning_id))} />
                  </Td>
                  <Td className="whitespace-nowrap text-slate-500">{dmy(r.invoice_date)}</Td>
                  <Td className="font-medium text-slate-900">{r.referral_name}
                    <span className="block text-xs capitalize text-slate-400">{r.referral_type}</span></Td>
                  <Td className="font-mono-num text-slate-600">{r.invoice_no}</Td>
                  <Td className="text-slate-600">{r.customer_name || "—"}</Td>
                  <Td className="text-slate-600">{inr(r.bill_amount)}</Td>
                  <Td className="text-slate-500">{r.percentage}%</Td>
                  <Td className="font-semibold text-slate-900">{inr(r.referral_amount)}</Td>
                  <Td>
                    <span className={`text-xs font-semibold uppercase tracking-wider ${
                      r.paid ? "text-emerald-700" : "text-amber-700"}`}>
                      {r.paid ? "Paid" : "Unpaid"}
                    </span>
                  </Td>
                </tr>
              )} />
          </>
        )}
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} wide
        title={editing ? "Edit referral" : "Add referral"}
        subtitle="The percentage is applied to the net bill amount after discount."
        testid="referral-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-3" data-testid="referral-form">
          <Field label="Referral type">
            <select className={inputCls} value={form.referral_type} data-testid="referral-type-select"
              onChange={(e) => setForm({ ...form, referral_type: e.target.value })}>
              <option value="doctor">Doctor</option>
              <option value="hospital">Hospital</option>
            </select>
          </Field>
          <Field label="Name *" className="sm:col-span-2">
            <input required minLength={2} className={inputCls} value={form.name} data-testid="referral-name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Referral percentage (%) *">
            <input required type="number" min="0" max="100" step="0.01" className={inputCls}
              value={form.percentage} data-testid="referral-percentage-input"
              onChange={(e) => setForm({ ...form, percentage: e.target.value })} />
          </Field>
          <Field label="Speciality / department">
            <input className={inputCls} value={form.speciality} data-testid="referral-speciality-input"
              onChange={(e) => setForm({ ...form, speciality: e.target.value })} />
          </Field>
          <Field label="Registration no.">
            <input className={inputCls} value={form.registration_no}
              onChange={(e) => setForm({ ...form, registration_no: e.target.value })} />
          </Field>
          <Field label="Contact person">
            <input className={inputCls} value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          </Field>
          <div><PhoneInput label="Mobile" value={form.phone} testid="referral-phone"
            onChange={(v) => setForm({ ...form, phone: v })} /></div>
          <Field label="Email">
            <input type="email" className={inputCls} value={form.email} data-testid="referral-email-input"
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Address" className="sm:col-span-2">
            <input className={inputCls} value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="City">
            <input className={inputCls} value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </Field>
          <Field label="Notes" className="sm:col-span-3">
            <input className={inputCls} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          {formError && <p className="text-sm text-red-600 sm:col-span-3" data-testid="referral-form-error">{formError}</p>}
          <div className="sm:col-span-3">
            <button className={primaryBtnCls} data-testid="referral-submit-btn">
              {editing ? "Update referral" : "Save referral"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirm)} onClose={() => setConfirm(null)}
        title={confirm?.status === "active" ? "Deactivate referral" : "Activate referral"}
        subtitle={confirm?.name} testid="referral-confirm-modal">
        <form onSubmit={(e) => { e.preventDefault(); toggleStatus(); }} className="space-y-5">
          <p className="text-sm text-slate-600">
            {confirm?.status === "active"
              ? "A deactivated referral stays on all historical invoices and reports but cannot be selected on new bills."
              : "The referral will be selectable on new bills again."}
          </p>
          {confirm?.status === "active" && (
            <Field label="Reason * (recorded in the audit log)">
              <input required minLength={5} className={inputCls} value={reason}
                data-testid="referral-reason-input" onChange={(e) => setReason(e.target.value)} />
            </Field>
          )}
          <button className={primaryBtnCls} data-testid="referral-confirm-btn">
            {confirm?.status === "active" ? "Deactivate" : "Activate"}
          </button>
        </form>
      </Modal>

      <PrintPreview open={print} onClose={() => setPrint(false)}
        title={`Referral report ${MONTHS[month - 1]} ${year}`} testid="referral-print-preview">
        {report && (
          <>
            <DocHeader business={business} docTitle="Referral Report"
              right={<p className="text-[11px]">{MONTHS[month - 1]} {year}</p>} />
            <table className="mt-6 w-full border-collapse text-[11.5px]">
              <thead>
                <tr className="border-y-2 border-slate-800 text-left">
                  <th className="py-1.5">Date</th><th className="py-1.5">Referral</th>
                  <th className="py-1.5">Invoice</th><th className="py-1.5">Customer</th>
                  <th className="py-1.5 text-right">Bill</th><th className="py-1.5 text-right">%</th>
                  <th className="py-1.5 text-right">Referral</th><th className="py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.earning_id} className="border-b border-slate-200">
                    <td className="py-1.5">{dmy(r.invoice_date)}</td>
                    <td className="py-1.5 font-medium">{r.referral_name}</td>
                    <td className="py-1.5">{r.invoice_no}</td>
                    <td className="py-1.5">{r.customer_name || "—"}</td>
                    <td className="py-1.5 text-right">{inr(r.bill_amount)}</td>
                    <td className="py-1.5 text-right">{r.percentage}%</td>
                    <td className="py-1.5 text-right font-medium">{inr(r.referral_amount)}</td>
                    <td className="py-1.5">{r.paid ? "Paid" : "Unpaid"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 flex justify-end">
              <div className="w-72 space-y-1 text-[12px]">
                <div className="flex justify-between"><span className="text-slate-600">Bill amount</span><span>{inr(report.totals.bill_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Paid</span><span>{inr(report.totals.paid)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Unpaid</span><span>{inr(report.totals.unpaid)}</span></div>
                <div className="flex justify-between border-y-2 border-slate-800 py-1.5 font-ui text-[14px] font-bold">
                  <span>Total referral</span><span>{inr(report.totals.referral_amount)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </PrintPreview>
    </div>
  );
}
